import React from "react";
import { t } from "ttag";
import { Spinner, Table } from "@coolremote/shared";
import {
  useCreateModbusUiTemplate,
  useDeleteModbusUiTemplate,
  useGetSiteModbusUiTemplates,
  useRenameModbusUiTemplate,
} from "../../../../hooks/useModbusUiTemplatesApi";
import { IModbusUiTemplate } from "../../types";
import type { IUIControlPoint } from "../../UITemplateMapper";
import LibraryActionBanner from "./LibraryActionBanner";
import LibraryToolbar from "./LibraryToolbar";
import { buildCheckboxColumn, buildDeleteActionColumn, buildNameColumn } from "./libraryColumns";
import { ILibrarySaveApi, useLibraryTab } from "./useLibraryTab";
import styles from "./ModbusSiteLibraryManagementDialog.module.css";

interface IProps {
  siteId?: string;
  active: boolean;
  /** Lets the dialog drive this tab's Save from its own footer button. */
  onSaveApiChange?: (api: ILibrarySaveApi | null) => void;
}

const paramNames = (template: IModbusUiTemplate): string[] =>
  ((template.data?.points ?? []) as IUIControlPoint[]).map((p) => p.labelName?.trim()).filter(Boolean) as string[];

/**
 * The dedicated modbus-ui-templates collection has no real link to a functional
 * template (see ModbusUiTemplateModel — no functionalTemplate ref), so this is
 * descriptive metadata carried in the entry's own `data` blob (set on import,
 * or left blank), not a live lookup.
 */
const functionalUnitName = (template: IModbusUiTemplate): string => template.data?.functionalUnitName?.trim() || "–";

const ControlUiLibraryTab: React.FC<IProps> = ({ siteId, active, onSaveApiChange }) => {
  const { mutate: createUiTemplate } = useCreateModbusUiTemplate({});
  const { mutate: renameUiTemplate } = useRenameModbusUiTemplate({});
  const { mutate: deleteUiTemplate } = useDeleteModbusUiTemplate({});
  const { data, isLoading, refetch } = useGetSiteModbusUiTemplates(siteId, active) as unknown as {
    data: IModbusUiTemplate[] | null;
    isLoading: boolean;
    refetch: () => void;
  };

  const lib = useLibraryTab<IModbusUiTemplate>({
    items: data ?? [],
    refetch,
    active,
    siteId,
    create: createUiTemplate,
    rename: (templateId, name) => renameUiTemplate({ templateId, name }),
    remove: deleteUiTemplate,
    pickExportFields: ({ name, description, deviceVendor, deviceVendorId, data: templateData }) => ({
      name, description, deviceVendor, deviceVendorId, data: templateData,
    }),
    importedItemsLabel: t`control`,
    onSaveApiChange,
  });

  return (
    <div className={styles.tabRoot}>
      <LibraryToolbar
        search={lib.search}
        onSearchChange={lib.setSearch}
        exportMode={lib.exportMode}
        selectedCount={lib.selectedIds.size}
        onEnterExportMode={() => lib.setExportMode(true)}
        onExitExportMode={lib.exitExportMode}
        onExportSelected={lib.handleExportSelected}
        siteId={siteId}
        importing={lib.importing}
        importProgress={lib.importProgress}
        fileInputRef={lib.fileInputRef}
        onFileChange={lib.handleFileChange}
      />
      <LibraryActionBanner banner={lib.banner} />

      <div className={styles.tabBody}>
      {/* Spinner only on the first load — a background refetch (e.g. after a
          rename) keeps the table on screen instead of blanking the tab. */}
      {isLoading && !data ? (
        <div className={styles.loaderWrap}>
          <Spinner size={28} />
        </div>
      ) : (
        <Table
          stickyHeader
          data={lib.filtered}
          getRowKey={(row: IModbusUiTemplate, index: number) => row.id ?? index}
          emptyMessage={lib.search ? t`No control UIs match your search.` : t`No control UIs found for this site.`}
          columns={[
            ...buildCheckboxColumn(lib.exportMode, lib.allSelected, lib.toggleAll, lib.selectedIds, lib.toggleSelected),
            ...buildNameColumn<IModbusUiTemplate>(t`CONTROL UI NAME`, "25%", lib.editingNames, lib.setDraftName, lib.pendingDeleteIds),
            {
              id: "unitName",
              title: t`FUNCTIONAL UNIT NAME`,
              width: "25%",
              render: (row: IModbusUiTemplate) => functionalUnitName(row),
            },
            {
              id: "mappedParams",
              title: t`MAPPED PARAMETERS`,
              width: lib.exportMode ? "40%" : "30%",
              render: (row: IModbusUiTemplate) => {
                const params = paramNames(row);
                return (
                  <div className={styles.paramList} title={params.join(", ")}>
                    {params.length > 0 ? params.join(", ") : "–"}
                  </div>
                );
              },
            },
            ...buildDeleteActionColumn(lib.exportMode, lib.pendingDeleteIds, lib.togglePendingDelete),
          ]}
        />
      )}
      </div>
    </div>
  );
};

export default ControlUiLibraryTab;
